import {
  INSURANCE_TYPE_ALIAS,
  INSURANCE_TYPES_ICONS_V2,
} from 'constants/insurance';
import { isLifeReferral } from './marketScan';
import { MARKET_SCAN_AVERAGE_SAVINGS } from 'constants/marketScan';
import { INSURANCE_TYPES_NAMES } from 'constants/insurance-types-names';
import { IUser } from 'types';

const getSubtitle = (policyType: string) => {
  switch (policyType) {
    case INSURANCE_TYPES_NAMES.PET:
      return 'Exclusive offer';
    case INSURANCE_TYPES_NAMES.TRAVEL:
      return 'Quick quote';
    default:
      if (isLifeReferral(policyType)) {
        return 'Compare options';
      }
      const savings = MARKET_SCAN_AVERAGE_SAVINGS[policyType];
      return savings ? `Avg. savings ${savings}` : '';
  }
};

export const getPolicyTypesToShow = (selectedPolicies: string[] = []) => {
  const otherSelected =
    selectedPolicies.find(
      (p) =>
        ![
          INSURANCE_TYPES_NAMES.VEHICLE,
          INSURANCE_TYPES_NAMES.HOME_CONTENTS,
          INSURANCE_TYPES_NAMES.HOME,
          INSURANCE_TYPES_NAMES.CONTENT,
        ].includes(p),
    ) || INSURANCE_TYPES_NAMES.HEALTH;

  return [
    {
      key: INSURANCE_TYPES_NAMES.VEHICLE,
      title: INSURANCE_TYPES_NAMES.VEHICLE,
      subtitle: `Avg. savings ${MARKET_SCAN_AVERAGE_SAVINGS[INSURANCE_TYPES_NAMES.VEHICLE]
        }`,
      image: INSURANCE_TYPES_ICONS_V2[INSURANCE_TYPES_NAMES.VEHICLE],
    },
    {
      key: INSURANCE_TYPES_NAMES.HOME_CONTENTS,
      title: INSURANCE_TYPES_NAMES.HOME_CONTENTS,
      subtitle: `Avg. savings ${MARKET_SCAN_AVERAGE_SAVINGS[INSURANCE_TYPES_NAMES.HOME_CONTENTS]
        }`,
      image: INSURANCE_TYPES_ICONS_V2[INSURANCE_TYPES_NAMES.HOME_CONTENTS],
    },
    {
      key: otherSelected,
      title:
        otherSelected === INSURANCE_TYPES_NAMES.LIFE
          ? 'Life insurance'
          : INSURANCE_TYPE_ALIAS[otherSelected] || otherSelected,
      subtitle: getSubtitle(otherSelected),
      image: INSURANCE_TYPES_ICONS_V2[otherSelected],
    },
  ];
};

export const policyTypesToShow = [
  {
    key: INSURANCE_TYPES_NAMES.VEHICLE,
    title: INSURANCE_TYPES_NAMES.VEHICLE,
    subtitle: MARKET_SCAN_AVERAGE_SAVINGS[INSURANCE_TYPES_NAMES.VEHICLE],
    image: INSURANCE_TYPES_ICONS_V2[INSURANCE_TYPES_NAMES.VEHICLE],
  },
  {
    key: INSURANCE_TYPES_NAMES.HOME_CONTENTS,
    title: INSURANCE_TYPES_NAMES.HOME_CONTENTS,
    subtitle: MARKET_SCAN_AVERAGE_SAVINGS[INSURANCE_TYPES_NAMES.HOME_CONTENTS],
    image: INSURANCE_TYPES_ICONS_V2[INSURANCE_TYPES_NAMES.HOME_CONTENTS],
  },
  {
    key: INSURANCE_TYPES_NAMES.CONTENT,
    title: INSURANCE_TYPES_NAMES.CONTENT,
    subtitle: MARKET_SCAN_AVERAGE_SAVINGS[INSURANCE_TYPES_NAMES.CONTENT],
    image: INSURANCE_TYPES_ICONS_V2[INSURANCE_TYPES_NAMES.CONTENT],
  },
];

// Types that can be compared via Market Scan
const COMPARABLE_TYPES = [
  INSURANCE_TYPES_NAMES.VEHICLE,
  INSURANCE_TYPES_NAMES.MOTORCYCLE,
  INSURANCE_TYPES_NAMES.HOME,
  INSURANCE_TYPES_NAMES.CONTENT,
  INSURANCE_TYPES_NAMES.HOME_CONTENTS,
  INSURANCE_TYPES_NAMES.LANDLORD,
  INSURANCE_TYPES_NAMES.LIFE,
  INSURANCE_TYPES_NAMES.RV,
];

const DEFAULT_OPTIONS = [
  INSURANCE_TYPES_NAMES.VEHICLE,
  INSURANCE_TYPES_NAMES.HOME_CONTENTS,
  INSURANCE_TYPES_NAMES.CONTENT,
];

/**
 * Builds an array of policy type card data objects from an array of type keys.
 * Note: The card component appends "Insurance" after the title, so we use "Life" not "Life insurance"
 */
const buildPolicyTypeCards = (types: string[]) => {
  return types.map((type) => ({
    key: type,
    title: type === INSURANCE_TYPES_NAMES.LIFE ? 'Life' : type,
    subtitle: MARKET_SCAN_AVERAGE_SAVINGS[type] || '',
    image: INSURANCE_TYPES_ICONS_V2[type],
  }));
};

/**
 * Returns 3 unique policy types to show on the carousel based on user's onboarding selections.
 *
 * Rules:
 * - Users with no non-default comparable selections: Always show defaults
 * - Users with non-default selections: Show personalized options (excluding current/scanned)
 * - All 3 cards must be unique (no duplicates on the carousel)
 * - When user exhausts all options: Fall back to showing all 3 defaults
 */
export const getComparablePolicyTypesToShow = (
  user: IUser | undefined,
  currentPolicyType?: string,
  alreadyScannedTypes: string[] = []
) => {
  const defaultsSet = new Set(DEFAULT_OPTIONS);
  const scannedTypesSet = new Set(alreadyScannedTypes);

  // Check if user has any non-default comparable selections
  const hasNonDefaultSelections = (user?.insuranceTypes || []).some(
    (type) => COMPARABLE_TYPES.includes(type) && !defaultsSet.has(type)
  );

  // If no non-default selections, always show defaults
  if (!hasNonDefaultSelections) {
    return buildPolicyTypeCards(DEFAULT_OPTIONS);
  }

  // Build personalized options
  const selectedOptions = new Set<string>();

  const addIfValid = (type: string) => {
    if (
      selectedOptions.size < 3 &&
      COMPARABLE_TYPES.includes(type) &&
      type !== currentPolicyType &&
      !scannedTypesSet.has(type) &&
      !selectedOptions.has(type)
    ) {
      selectedOptions.add(type);
    }
  };

  // 1. Add user's selections (preserves order)
  user?.insuranceTypes?.forEach(addIfValid);

  // 2. Fill from defaults
  DEFAULT_OPTIONS.forEach(addIfValid);

  // 3. Fill from other comparable types
  COMPARABLE_TYPES.forEach(addIfValid);

  // If we couldn't get 3, fall back to defaults
  return selectedOptions.size < 3
    ? buildPolicyTypeCards(DEFAULT_OPTIONS)
    : buildPolicyTypeCards(Array.from(selectedOptions));
};

